/* eslint-disable react/prop-types, prettier/prettier */
import React, { useState, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { keyframes } from '@emotion/react'
import {
    Box,
    Typography,
    TextField,
    Chip,
    Collapse,
    IconButton,
    MenuItem,
    Paper,
    Button,
} from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import WarningIcon from '@mui/icons-material/Warning'
import ErrorIcon from '@mui/icons-material/Error'
import GppMaybeIcon from '@mui/icons-material/GppMaybe'
import SendIcon from '@mui/icons-material/Send'
import AIService from '../../services/aiService'
import GlowButton from '../../components/shared/GlowButton'
import ErrorBanner from '../../components/shared/ErrorBanner'
import { parseCSV } from '../../utils/csv'
import { variableExample } from '../../helpers/templateForm'
import { channelFrom } from '../../helpers/aiHandoff'
import {
    ACCENT,
    CATEGORY,
    FONT,
    ICON,
    RADIUS,
    STATUS,
    SURFACE,
    TEXT,
    cardSx,
    pageSx,
    tint,
} from '../../theme/tokens'

const shimmer = keyframes`
  0%{background-position:-400px 0} 100%{background-position:400px 0}
`

const CHANNELS = ['whatsapp', 'email', 'sms']

// A preview row's state: its STATUS pair (text, tint, border).
export const ROW_STATUS = {
    ok: { tone: STATUS.success, label: 'OK', Icon: CheckCircleIcon },
    warning: { tone: STATUS.warning, label: 'Warning', Icon: WarningIcon },
    error: { tone: STATUS.error, label: 'Error', Icon: ErrorIcon },
}

function ShimmerBlock({ height = 60, borderRadius = RADIUS.md }) {
    return (
        <Box
            sx={{
                height,
                borderRadius: `${borderRadius}px`,
                overflow: 'hidden',
                background: `linear-gradient(90deg, ${SURFACE.sunken} 25%, ${SURFACE.border} 50%, ${SURFACE.sunken} 75%)`,
                backgroundSize: '800px 100%',
                animation: `${shimmer} 1.5s infinite linear`,
            }}
        />
    )
}

/** Adapt shared RFC 4180 parser to this page's shape; null when unusable. */
function parseCsvInput(text) {
    const { headers, rows } = parseCSV(text)
    const columns = headers.filter(Boolean)
    if (columns.length === 0 || rows.length === 0) return null
    return { columns, rowCount: rows.length, rows }
}

function SummaryBar({ summary }) {
    const items = [
        { label: 'Total', value: summary.total, color: ACCENT.text },
        { label: 'Ready', value: summary.ok, color: STATUS.success.fg },
        {
            label: 'Warnings',
            value: summary.warnings,
            color: STATUS.warning.fg,
        },
        { label: 'Errors', value: summary.errors, color: STATUS.error.fg },
    ]
    return (
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 3 }}>
            {items.map(({ label, value, color }) => (
                <Box
                    key={label}
                    sx={{
                        ...cardSx,
                        flex: 1,
                        minWidth: 100,
                        p: 2,
                        textAlign: 'center',
                    }}
                >
                    <Typography variant="h1" sx={{ color, lineHeight: 1 }}>
                        {value}
                    </Typography>
                    <Typography
                        variant="caption"
                        component="p"
                        sx={{ color: TEXT.faint, mt: 0.5 }}
                    >
                        {label}
                    </Typography>
                </Box>
            ))}
        </Box>
    )
}

function PreviewRow({ preview, expanded, onToggle, channel }) {
    const { tone, label, Icon } = ROW_STATUS[preview.status] || ROW_STATUS.ok
    const rendered = preview.rendered ?? ''
    const missingVars = preview.missing_vars ?? []
    return (
        <Box
            sx={{
                border: `1px solid ${SURFACE.border}`,
                borderRadius: `${RADIUS.md}px`,
                mb: 1,
                overflow: 'hidden',
                background: SURFACE.card,
                transition: 'border-color .2s',
                '&:hover': { borderColor: SURFACE.borderHover },
            }}
        >
            <Box
                role="button"
                tabIndex={0}
                aria-expanded={expanded}
                onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault()
                        onToggle()
                    }
                }}
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    p: '10px 16px',
                    cursor: 'pointer',
                }}
                onClick={onToggle}
            >
                <Typography
                    variant="caption"
                    sx={{ color: TEXT.faint, minWidth: 28 }}
                >
                    #{preview.row}
                </Typography>
                <Typography
                    variant="body2"
                    sx={{
                        flex: 1,
                        color: TEXT.secondary,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                    }}
                >
                    {rendered.slice(0, 90)}
                    {rendered.length > 90 ? '…' : ''}
                </Typography>
                <Chip
                    label={`${preview.char_count} chars`}
                    size="small"
                    sx={{
                        background: SURFACE.hover,
                        color: TEXT.muted,
                        typography: 'caption',
                    }}
                />
                <Chip
                    icon={
                        <Icon
                            sx={{
                                fontSize: `${ICON.sm}px !important`,
                                color: `${tone.fg} !important`,
                            }}
                        />
                    }
                    label={label}
                    size="small"
                    sx={{
                        background: tone.bg,
                        color: tone.fg,
                        border: `1px solid ${tone.border}`,
                        typography: 'caption',
                    }}
                />
                <IconButton
                    size="small"
                    aria-label={
                        expanded ? 'Collapse row preview' : 'Expand row preview'
                    }
                    aria-expanded={expanded}
                    tabIndex={-1}
                    sx={{ color: TEXT.faint, p: 0 }}
                >
                    {expanded ? (
                        <ExpandLessIcon fontSize="small" />
                    ) : (
                        <ExpandMoreIcon fontSize="small" />
                    )}
                </IconButton>
            </Box>
            <Collapse in={expanded}>
                <Box sx={{ px: 2, pb: 2 }}>
                    {missingVars.length > 0 && (
                        <Box
                            sx={{
                                mb: 1.5,
                                display: 'flex',
                                flexWrap: 'wrap',
                                gap: 0.5,
                            }}
                        >
                            <Typography
                                variant="caption"
                                sx={{ color: STATUS.error.fg, mr: 0.5 }}
                            >
                                Missing:
                            </Typography>
                            {missingVars.map((v) => (
                                <Chip
                                    key={v}
                                    label={variableExample(channel, v)}
                                    size="small"
                                    sx={{
                                        background: STATUS.error.bg,
                                        color: STATUS.error.fg,
                                        typography: 'caption',
                                    }}
                                />
                            ))}
                        </Box>
                    )}
                    <Box
                        sx={{
                            background: SURFACE.subtle,
                            borderRadius: `${RADIUS.sm}px`,
                            p: 1.5,
                            border: `1px solid ${SURFACE.border}`,
                        }}
                    >
                        <Typography
                            variant="body2"
                            sx={{
                                color: TEXT.secondary,
                                whiteSpace: 'pre-wrap',
                                fontFamily: FONT.mono,
                                lineHeight: 1.6,
                            }}
                        >
                            {rendered}
                        </Typography>
                    </Box>
                </Box>
            </Collapse>
        </Box>
    )
}

export default function BulkPreview() {
    const navigate = useNavigate()
    const location = useLocation()
    const [templateBody, setTemplateBody] = useState('')
    // Decides the variable syntax the preview fills, the same way the real
    // send does: {{name}} for WhatsApp (Meta), ${name} for everything else
    // (the sender's string.Template). WhatsApp is the default so a pasted
    // {{...}} template keeps previewing as before.
    const [channel, setChannel] = useState('whatsapp')
    const [csvText, setCsvText] = useState('')
    const [loading, setLoading] = useState(false)
    const [result, setResult] = useState(null)
    const [error, setError] = useState('')
    const [expandedRow, setExpandedRow] = useState(null)

    useEffect(() => {
        // Reset outputs so re-runs (navigation with new state) behave predictably
        setResult(null)
        setError('')
        setExpandedRow(null)
        if (location.state?.template_body) {
            setTemplateBody(location.state.template_body)
        }
        // A template handed over from Template Writer or A/B Variants keeps
        // its channel; without it an email's ${name} previewed as WhatsApp.
        if (location.state?.channel) {
            setChannel(
                channelFrom(location.state.channel, CHANNELS, 'whatsapp')
            )
        }
    }, [location.state])

    const parsed = parseCsvInput(csvText)
    const canPreview = templateBody.trim() && parsed && parsed.rowCount > 0

    const handlePreview = async () => {
        if (!canPreview) return
        setLoading(true)
        setError('')
        setResult(null)
        setExpandedRow(null)
        try {
            const res = await AIService.bulkPreview({
                template_body: templateBody,
                rows: parsed.rows,
                channel,
            })
            setResult(res.data.result)
        } catch {
            setError('Preview failed. Please try again.')
        } finally {
            setLoading(false)
        }
    }

    const dotGrid = {
        backgroundImage: `radial-gradient(${SURFACE.border} 1px, transparent 1px)`,
        backgroundSize: '28px 28px',
    }

    return (
        <Box
            sx={{
                ...pageSx,
                ...dotGrid,
                position: 'relative',
                overflow: 'hidden',
            }}
        >
            <Box
                sx={{
                    position: 'absolute',
                    top: -120,
                    right: -80,
                    width: 500,
                    height: 500,
                    borderRadius: '50%',
                    background: `radial-gradient(circle, ${ACCENT.bg12} 0%, transparent 70%)`,
                    pointerEvents: 'none',
                }}
            />
            <Box
                sx={{
                    position: 'absolute',
                    bottom: -100,
                    left: -60,
                    width: 400,
                    height: 400,
                    borderRadius: '50%',
                    background: `radial-gradient(circle, ${tint(
                        CATEGORY.cyan,
                        0.08
                    )} 0%, transparent 70%)`,
                    pointerEvents: 'none',
                }}
            />

            <Box
                sx={{
                    position: 'relative',
                    zIndex: 1,
                    maxWidth: 1100,
                    mx: 'auto',
                    p: { xs: 3, md: 5 },
                }}
            >
                <Box sx={{ mb: 4 }}>
                    <Typography
                        variant="h3"
                        component="h1"
                        sx={{ color: TEXT.primary }}
                    >
                        Bulk Preview
                    </Typography>
                    <Typography
                        variant="body1"
                        sx={{ color: TEXT.faint, mt: 0.5 }}
                    >
                        Paste your template and CSV — see every message rendered
                        before you send.
                    </Typography>
                </Box>

                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                        gap: 3,
                        mb: 3,
                    }}
                >
                    <Paper sx={{ ...cardSx, p: 3 }} elevation={0}>
                        <Typography
                            variant="overline"
                            component="p"
                            sx={{ color: ACCENT.text, mb: 1.5 }}
                        >
                            Message Template
                        </Typography>
                        <TextField
                            select
                            size="small"
                            label="Channel"
                            value={channel}
                            onChange={(e) => {
                                setChannel(e.target.value)
                                setResult(null)
                            }}
                            sx={{ mb: 1.5, minWidth: 180 }}
                        >
                            <MenuItem value="whatsapp">WhatsApp</MenuItem>
                            <MenuItem value="email">Email</MenuItem>
                            <MenuItem value="sms">SMS</MenuItem>
                        </TextField>
                        <TextField
                            multiline
                            rows={10}
                            fullWidth
                            value={templateBody}
                            onChange={(e) => setTemplateBody(e.target.value)}
                            inputProps={{ 'aria-label': 'Message template' }}
                            placeholder={`Hi ${variableExample(
                                channel
                            )},\n\nYour ${variableExample(
                                channel,
                                'batch_name'
                            )} class starts at ${variableExample(
                                channel,
                                'session_time'
                            )}.\n\nJoin here: ${variableExample(
                                channel,
                                'join_link'
                            )}`}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    typography: 'body2',
                                    fontFamily: FONT.mono,
                                },
                            }}
                        />
                        <Typography
                            variant="caption"
                            component="p"
                            sx={{ color: TEXT.faint, mt: 1 }}
                        >
                            Use {variableExample(channel, 'variable_name')} for
                            placeholders; the name must match a CSV column
                            header exactly.
                        </Typography>
                    </Paper>

                    <Paper sx={{ ...cardSx, p: 3 }} elevation={0}>
                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                mb: 1.5,
                            }}
                        >
                            <Typography
                                variant="overline"
                                sx={{ color: CATEGORY.cyan }}
                            >
                                CSV Data
                            </Typography>
                            {parsed && (
                                <Chip
                                    label={`${parsed.rowCount} row${
                                        parsed.rowCount === 1 ? '' : 's'
                                    } · ${parsed.columns.length} col${
                                        parsed.columns.length === 1 ? '' : 's'
                                    }`}
                                    size="small"
                                    sx={{
                                        background: tint(CATEGORY.cyan, 0.12),
                                        color: CATEGORY.cyan,
                                        typography: 'caption',
                                    }}
                                />
                            )}
                        </Box>
                        <TextField
                            multiline
                            rows={10}
                            fullWidth
                            value={csvText}
                            onChange={(e) => {
                                setCsvText(e.target.value)
                                setResult(null)
                            }}
                            inputProps={{ 'aria-label': 'CSV data' }}
                            placeholder={
                                'name,batch_name,session_time,join_link\nAlex,Math Pro,5:00 PM,https://link.bhanzu.com/abc\nPriya,Science Elite,6:00 PM,https://link.bhanzu.com/xyz'
                            }
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    typography: 'caption',
                                    fontFamily: FONT.mono,
                                },
                            }}
                        />
                        {parsed && (
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexWrap: 'wrap',
                                    gap: 0.5,
                                    mt: 1,
                                }}
                            >
                                {parsed.columns.map((col) => (
                                    <Chip
                                        key={col}
                                        label={variableExample(channel, col)}
                                        size="small"
                                        sx={{
                                            background: tint(
                                                CATEGORY.cyan,
                                                0.1
                                            ),
                                            color: CATEGORY.cyan,
                                            typography: 'caption',
                                            height: 20,
                                        }}
                                    />
                                ))}
                            </Box>
                        )}
                    </Paper>
                </Box>

                <Box sx={{ display: 'flex', justifyContent: 'center', mb: 4 }}>
                    <GlowButton
                        onClick={handlePreview}
                        disabled={!canPreview}
                        loading={loading}
                    >
                        Preview{' '}
                        {parsed ? `${parsed.rowCount} Messages` : 'Messages'}
                    </GlowButton>
                </Box>

                {error && (
                    <Box sx={{ mb: 3 }}>
                        <ErrorBanner message={error} />
                    </Box>
                )}

                {loading && (
                    <Box sx={{ display: 'grid', gap: 1 }}>
                        {[80, 64, 64, 80].map((h, i) => (
                            <ShimmerBlock key={i} height={h} />
                        ))}
                    </Box>
                )}

                {result && (
                    <Box>
                        {result.summary && (
                            <SummaryBar summary={result.summary} />
                        )}
                        {(result.previews ?? []).map((preview) => (
                            <PreviewRow
                                key={preview.row}
                                preview={preview}
                                channel={channel}
                                expanded={expandedRow === preview.row}
                                onToggle={() =>
                                    setExpandedRow(
                                        expandedRow === preview.row
                                            ? null
                                            : preview.row
                                    )
                                }
                            />
                        ))}
                        <Box
                            sx={{
                                display: 'flex',
                                gap: 1,
                                mt: 2,
                                flexWrap: 'wrap',
                            }}
                        >
                            <Button
                                variant="outlined"
                                size="small"
                                startIcon={<GppMaybeIcon />}
                                onClick={() =>
                                    navigate('/ai/unsubscribe-risk', {
                                        state: {
                                            template_body: templateBody,
                                            channel,
                                        },
                                    })
                                }
                            >
                                Check Unsubscribe Risk
                            </Button>
                            <Button
                                variant="outlined"
                                size="small"
                                startIcon={<SendIcon />}
                                onClick={() =>
                                    navigate('/bulk-send', {
                                        state: { csvText: csvText },
                                    })
                                }
                            >
                                Proceed to Bulk Send
                            </Button>
                        </Box>
                    </Box>
                )}
            </Box>
        </Box>
    )
}
